/**
 * One-state State Portal — Phase 1 data adapter.
 *
 * Gold Vue screens still call the same endpoint names (CONNECTIONS.json).
 * This adapter:
 *  - Pins instance identity by SOURCE_STATE
 *  - Supplies fixtures when VITE_STATE_PORTAL_USE_FIXTURES=1 (or when APIs are unavailable)
 *  - Never writes fixture shapes into live state collections
 *
 * PHASE2: swap fixture returns for live axios against learn-server routes listed below.
 */

import sourceStates from '@/components/state-portal-one-state/SOURCE_STATES.json'
import {
  getFixtureOrganizations,
  getFixtureLearners,
  getFixtureUsers,
  getFixtureResourcesDoc,
  getFixtureSports,
  getFixtureSelectorOrganizations,
  getFixtureRewards,
  getFixtureReportSeries,
} from './statePortalFixtures.js'

export const DASHBOARD_PEOPLE_ORGS_BODY_FIELD = 'state_user_has_access_to_state_portal'

export function listAllowedSourceStates() {
  return { ...sourceStates.states }
}

export function geographyStateForMaps(sourceState) {
  return String(sourceState || '').trim().toUpperCase()
}

export function isBtSandbox() {
  return false
}

export function useFixtures() {
  return String(import.meta.env.VITE_STATE_PORTAL_USE_FIXTURES || '') === '1'
}

/**
 * Resolve association companyID for a SOURCE_STATE from selector-shaped orgs.
 * @param {string} sourceState
 * @param {Array<{state?: string, companyID?: string}>} organizations
 */
export function resolveOrganizationId(sourceState, organizations = []) {
  const code = String(sourceState || '').trim().toUpperCase()
  const match = (organizations || []).find(
    (org) => String(org?.state || '').trim().toUpperCase() === code && org?.companyID
  )
  return match?.companyID || ''
}

/**
 * Session pin payload — state only.
 * PHASE2: POST /set-current-state-portal
 */
export function buildSetCurrentBody(sourceState) {
  return {
    state: String(sourceState || '').trim().toUpperCase(),
  }
}

/**
 * Critical: dashboard people/orgs must NOT send `{ state }` alone.
 * PHASE2: POST /get-state-portal-organizations|get-users-for-state-portal|get-learners-for-state-portal
 */
export function buildDashboardPeopleOrgsBody(sourceState, extra = {}) {
  return {
    [DASHBOARD_PEOPLE_ORGS_BODY_FIELD]: String(sourceState || '').trim().toUpperCase(),
    ...extra,
  }
}

/** Browser keys that must include {STATE} — see CONNECTIONS.json */
export function browserKey(template, { state, userId, orgId } = {}) {
  const STATE = String(state || '').trim().toUpperCase()
  return String(template)
    .replaceAll('{STATE}', STATE)
    .replaceAll('{USER_ID}', String(userId || ''))
    .replaceAll('{ORG_ID}', String(orgId || ''))
    .replaceAll('{ORG}', String(orgId || ''))
}

export const STATE_SCOPED_BROWSER_KEYS = {
  dashboardLayout: 'statePortal.dashboardLayout.{USER_ID}.{STATE}',
  schedulerConnectors: 'statePortal.schedulerConnectors.{USER_ID}.{STATE}',
  calendarConnectors: 'statePortal.calendarConnectors.{USER_ID}.{STATE}',
  webinarSchedule: 'state-portal-webinar-schedule:{STATE}',
  journeyMaps: 'statePortal.journeyMaps.{ORG}.{STATE}',
  journeyMap: 'statePortal.journeyMap.{ORG}.{STATE}',
  mentorshipGeneral: 'statePortal.mentorship.{ORG}.{STATE}.general',
  mentorshipSport: 'statePortal.mentorship.{ORG}.{STATE}.{SPORT}',
  marketingBranding: 'statePortal.marketingBranding.{ORG_ID}.{STATE}',
}

/**
 * Phase 1 fixture loaders keyed by pinned SOURCE_STATE.
 * Per-abbr fixtures (never reuse another state's StatePortalResources doc).
 */
export const StatePortalDataAdapter = {
  async getSelectorOptions(states = []) {
    // PHASE2: POST /get-state-portal-selector-options { states }
    return {
      organizations: getFixtureSelectorOrganizations(states),
    }
  },

  async getOrganizations(sourceState) {
    // PHASE2: POST /get-state-portal-organizations { state_user_has_access_to_state_portal }
    return getFixtureOrganizations(sourceState)
  },

  async getUsers(sourceState) {
    // PHASE2: POST /get-users-for-state-portal { state_user_has_access_to_state_portal }
    return getFixtureUsers(sourceState)
  },

  async getLearners(sourceState) {
    // PHASE2: POST /get-learners-for-state-portal { state_user_has_access_to_state_portal }
    return getFixtureLearners(sourceState)
  },

  async getResources(sourceState) {
    // PHASE2: GET /state-portal-resources/:state  OR findOne({ state })
    // One document per abbreviation — never reuse another state's doc.
    return getFixtureResourcesDoc(sourceState)
  },

  async getSports(sourceState) {
    // PHASE2: POST /get-state-portal-sports { state }
    return getFixtureSports(sourceState)
  },

  async getRewards(sourceState) {
    // PHASE2: POST /get-state-portal-rewards { state }
    return getFixtureRewards(sourceState)
  },

  async getReportSeries(sourceState) {
    // PHASE2: POST /get-state-portal-report-series { state }
    return getFixtureReportSeries(sourceState)
  },

  /** Official Rank — live API only in production paths */
  async getOfficialRank(sourceState, sport) {
    // PHASE2: official-rank APIs on learn-server
    return {
      state: String(sourceState || '').trim().toUpperCase(),
      sport,
      awards: [],
      ratings: [],
      badgeSetup: {},
      usingBtMock: false,
    }
  },
}

/**
 * Optional axios response interceptor: when fixtures mode is on, fill known
 * State Portal GETs/POSTs without hitting live Jupiter writes.
 * Install from main.js only when VITE_STATE_PORTAL_USE_FIXTURES=1.
 */
export function installStatePortalFixtureInterceptor(axios) {
  if (!useFixtures() || !axios?.interceptors?.request) return () => {}

  const handler = async (config) => {
    const url = String(config.url || '')
    const method = String(config.method || 'get').toLowerCase()
    const data = typeof config.data === 'string'
      ? (() => { try { return JSON.parse(config.data) } catch { return {} } })()
      : (config.data || {})

    const stateFromBody = String(
      data.state_user_has_access_to_state_portal
      || data.state
      || data.portalState
      || ''
    ).trim().toUpperCase()

    const respond = (payload, status = 200) => {
      config.adapter = async () => ({
        data: payload,
        status,
        statusText: 'OK',
        headers: {},
        config,
        request: {},
      })
      return config
    }

    if (method === 'post' && url.includes('/get-state-portal-selector-options')) {
      return respond(await StatePortalDataAdapter.getSelectorOptions(data.states || []))
    }
    if (method === 'post' && url.includes('/get-state-portal-organizations') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getOrganizations(stateFromBody))
    }
    if (method === 'post' && url.includes('/get-users-for-state-portal') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getUsers(stateFromBody))
    }
    if (method === 'post' && url.includes('/get-learners-for-state-portal') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getLearners(stateFromBody))
    }
    if (method === 'post' && url.includes('/get-state-portal-sports') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getSports(stateFromBody))
    }
    if (method === 'post' && url.includes('/get-state-portal-rewards') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getRewards(stateFromBody))
    }
    if (method === 'post' && url.includes('/get-state-portal-report-series') && stateFromBody) {
      return respond(await StatePortalDataAdapter.getReportSeries(stateFromBody))
    }
    if (method === 'get' && /\/state-portal-resources\//.test(url)) {
      const abbr = url.split('/state-portal-resources/')[1]?.split(/[?#]/)[0]
      if (abbr) return respond(await StatePortalDataAdapter.getResources(abbr))
    }
    // Pin / session endpoints: acknowledge without writing live collections from fixtures mode
    if (method === 'post' && url.includes('/set-current-state-portal')) {
      return respond({ ok: true, ...buildSetCurrentBody(data.state) })
    }
    if (method === 'post' && url.includes('/set-active-state-portal-session')) {
      return respond({ ok: true, isInStatePortal: Boolean(data.isInStatePortal) })
    }

    return config
  }

  const id = axios.interceptors.request.use(handler)
  return () => axios.interceptors.request.eject(id)
}

export default StatePortalDataAdapter
